import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { BiX, BiTimeFive, BiRightArrowAlt, BiErrorCircle, BiLoaderAlt, BiMinus, BiPlus, BiInfoCircle } from "react-icons/bi";
import NumberField from "../../../Components/NumberField";
import Handler from "../../../Helpers/Handler";
import { RULES } from "../../../Helpers/bookingRules";
import { currencyFormatter } from "../../../Helpers/Utils";
import { openRazorpayCheckout } from "../../../Helpers/Razorpay";

// Phase B - Extension. One modal covers both paths a Started booking can be
// in (logic unchanged, only the presentation is new):
//  - a fresh extension: pick extra hours -> Get quote (extendCheck) ->
//    Pay (extendCreate creates the order, then Razorpay, then extendVerify).
//  - resuming an order that was created but never paid (prevExtendOrder) -
//    skips straight to Razorpay against that SAME order/amount instead of
//    calling extendCreate again, which would mint a duplicate order. Orders
//    created before extraHours was recorded ask for it inline as a fallback.
//
// Styling lives in Assets/css/style.css under .bvm. The bootstrap
// .modal wrapper is kept (same convention as the other Booking View modals)
// so the backdrop and stacking behave identically.

const PRESETS = [1, 2, 3, 6, 12, 24];
const HOUR_MS = 3600000;

const fmtReturn = (value) => {
    const d = value ? new Date(value) : null;
    if (!d || Number.isNaN(d.getTime())) return { day: "-", time: "" };
    return {
        day: d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" }),
        time: d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase(),
    };
};

const MAX_EXTEND_HOURS = RULES.EXTEND_HOURS_MAX;
const hoursLabel = (h) => `${Number(h)} hr${Number(h) === 1 ? "" : "s"}`;

const ExtendTripModal = ({ bookingId, customerName, customerPhone, prevExtendOrder, endDate, extendedHours = 0, extension = null, carLabel, onClose, onExtended }) => {
    const remainingHours = Math.max(0, MAX_EXTEND_HOURS - Number(extendedHours || 0));
    const resuming = !!prevExtendOrder;

    const [extraHours, setExtraHours] = useState("");
    const [resumeHours, setResumeHours] = useState(prevExtendOrder?.extraHours ? String(prevExtendOrder.extraHours) : "");
    const [quote, setQuote] = useState(null);
    const [quoting, setQuoting] = useState(false);
    const [quoteError, setQuoteError] = useState(null);
    const [paying, setPaying] = useState(false);
    // Sanity-check for the hand-typed resume path (see the big comment above):
    // there's no exact way to recover the hours an old, un-tagged order was
    // created for, but this re-prices whatever the admin types via the same
    // extend/check endpoint the fresh-extension path already uses, so they can
    // compare "does that estimate roughly match what this order was actually
    // charged" before paying - a mistyped value would otherwise silently
    // mis-record the trip's new end time with nothing to catch it.
    const [resumeEstimate, setResumeEstimate] = useState(null);
    const [resumeEstimating, setResumeEstimating] = useState(false);
    const [resumeEstimateError, setResumeEstimateError] = useState(null);

    const getQuote = async () => {
        if (!(Number.isInteger(Number(extraHours)) && Number(extraHours) > 0)) {
            toast.error("Enter whole hours (1 hour minimum)");
            return;
        }
        setQuoting(true);
        setQuoteError(null);
        setQuote(null);
        const response = await Handler({ method: "post", url: `/staff/flow/extend/check/${bookingId}`, data: { extraHours: Number(extraHours) } });
        setQuoting(false);
        if (response?.success && response?.data?.data) {
            setQuote(response.data.data);
        } else {
            setQuoteError(response?.data?.data?.message || response?.data?.message || "Could not price this extension");
        }
    };

    const finishPayment = async (checkoutResult, orderId, effectiveExtraHours) => {
        if (checkoutResult.status === "dismissed") {
            setPaying(false);
            return;
        }
        if (checkoutResult.status === "failed") {
            toast.error("Payment failed. You can try again.");
            setPaying(false);
            return;
        }
        if (checkoutResult.status === "error") {
            toast.error(checkoutResult.message);
            setPaying(false);
            return;
        }
        const razorpayResponse = checkoutResult.response;
        const verifyResponse = await Handler({
            method: "patch",
            url: `/staff/flow/extend/verify/${bookingId}`,
            data: {
                extraHours: effectiveExtraHours,
                orderId,
                paymentId: razorpayResponse.razorpay_payment_id,
                signature: razorpayResponse.razorpay_signature,
            },
        });
        setPaying(false);
        if (verifyResponse?.success) {
            toast.success("Trip extended");
            onExtended?.();
            onClose?.();
        } else {
            toast.error(
                verifyResponse?.data?.message ||
                    `Payment received but couldn't be applied. Contact support with payment ID ${razorpayResponse.razorpay_payment_id}.`
            );
        }
    };

    const payFresh = async () => {
        if (!quote) return;
        setPaying(true);
        const created = await Handler({ method: "post", url: `/staff/flow/extend/${bookingId}`, data: { extraHours: Number(extraHours) } });
        if (!created?.success || !created?.data?.data) {
            toast.error(created?.data?.message || "Could not create this extension order");
            setPaying(false);
            return;
        }
        const { orderId, amount } = created.data.data;
        const result = await openRazorpayCheckout({
            bookingId,
            orderId,
            amount,
            description: `Booking ${bookingId} · +${extraHours}h`,
            prefill: { name: customerName, contact: customerPhone },
        });
        await finishPayment(result, orderId, Number(extraHours));
    };

    const previewResumeHours = async () => {
        if (!(Number(resumeHours) > 0)) {
            toast.error("Enter the extra hours this order was created for");
            return;
        }
        setResumeEstimating(true);
        setResumeEstimateError(null);
        setResumeEstimate(null);
        const response = await Handler({ method: "post", url: `/staff/flow/extend/check/${bookingId}`, data: { extraHours: Number(resumeHours) } });
        setResumeEstimating(false);
        if (response?.success && response?.data?.data) {
            setResumeEstimate(response.data.data);
        } else {
            setResumeEstimateError(response?.data?.data?.message || response?.data?.message || "Could not estimate this");
        }
    };

    const payResume = async () => {
        if (!(Number(resumeHours) > 0)) {
            toast.error("Enter the extra hours this order was created for");
            return;
        }
        setPaying(true);
        const result = await openRazorpayCheckout({
            bookingId,
            orderId: prevExtendOrder.orderId,
            amount: prevExtendOrder.totalAmount,
            description: `Booking ${bookingId} extension (resumed)`,
            prefill: { name: customerName, contact: customerPhone },
        });
        await finishPayment(result, prevExtendOrder.orderId, Number(resumeHours));
    };

    const hoursNow = resuming ? Number(resumeHours) : Number(extraHours);
    const hoursValid = Number.isInteger(hoursNow) && hoursNow > 0 && hoursNow <= remainingHours;
    const current = fmtReturn(endDate);
    const nextEnd = endDate && hoursValid ? fmtReturn(new Date(new Date(endDate).getTime() + hoursNow * HOUR_MS)) : null;
    const dialogRef = useRef(null);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const cancelExtension = async () => {
        if (!prevExtendOrder?.orderId) return;
        setCancelling(true);
        const response = await Handler({ method: "post", url: `/staff/flow/${bookingId}/extension/${prevExtendOrder.orderId}/cancel` });
        setCancelling(false);
        if (response?.success) {
            toast.success("Extension cancelled");
            onExtended?.();
            onClose?.();
        } else {
            toast.error(response?.data?.message || "Could not cancel this extension");
        }
    };

    const busy = quoting || paying;

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape" && !paying) onClose?.(); };
        document.addEventListener("keydown", onKey);
        const first = dialogRef.current?.querySelector("input, button:not([disabled])");
        first?.focus();
        return () => document.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setHours = (value) => {
        const n = value === "" ? "" : Math.min(Number(value), remainingHours);
        setExtraHours(n === "" ? "" : String(n));
        setQuote(null);
        setQuoteError(null);
    };
    const nudge = (delta) => {
        const next = Math.max(0, Math.round((Number(extraHours) || 0) + delta));
        setHours(next || "");
    };

    const orderAmount = prevExtendOrder?.totalAmount ?? 0;
    const estimateMismatch = resumeEstimate && orderAmount > 0 && Math.abs((resumeEstimate.total ?? 0) - orderAmount) / orderAmount > 0.15;

    return (
        <>
            <div className="modal fade show" style={{ display: "block" }} tabIndex="-1" role="dialog" aria-modal="true" aria-labelledby="bvm-ext-title">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content bvm" ref={dialogRef}>
                        <div className="bvm-head">
                            <div>
                                <h5 id="bvm-ext-title">{resuming ? "Resume extension payment" : "Extend trip"}</h5>
                                <p><span className="bv-mono">{bookingId}</span>{carLabel ? ` · ${carLabel}` : ""}</p>
                            </div>
                            <button type="button" className="bvm-x" onClick={onClose} disabled={paying} aria-label="Close"><BiX aria-hidden="true" /></button>
                        </div>

                        <div className="bvm-body">
                            {/* Return time: now and after the extension */}
                            <div className="bvm-return" aria-live="polite">
                                <div>
                                    <span className="bv-eyebrow">Current return</span>
                                    <strong>{current.day}</strong>
                                    <span>{current.time}</span>
                                </div>
                                <div className="bvm-return-arrow" aria-hidden="true">
                                    {hoursValid ? <em>+{hoursLabel(hoursNow)}</em> : null}
                                    <BiRightArrowAlt />
                                </div>
                                <div className={nextEnd ? "is-new" : "is-empty"}>
                                    <span className="bv-eyebrow">New return</span>
                                    <strong>{nextEnd ? nextEnd.day : "Pick extra time"}</strong>
                                    <span>{nextEnd ? nextEnd.time : ""}</span>
                                </div>
                            </div>
                            <p className="bvm-hint"><BiTimeFive aria-hidden="true" /> {extendedHours > 0 ? `Already extended by ${hoursLabel(extendedHours)}. ` : ""}Maximum {MAX_EXTEND_HOURS} hrs per trip, {hoursLabel(remainingHours)} left.</p>
                            {extension ? (
                                <p className="bvm-hint" data-testid="extension-counters">
                                    {extension.used} of {extension.max} extensions used, {extension.hoursUsed} of {extension.hoursMax} hours used.
                                    {extension.latestEnd ? ` Latest end allowed by the next booking: ${fmtReturn(extension.latestEnd).day} ${fmtReturn(extension.latestEnd).time}.` : ""}
                                    {extension.canExtend === false && extension.reason ? ` ${extension.reason} Ask an admin if this needs an exception.` : ""}
                                </p>
                            ) : null}

                            {resuming ? (
                                <>
                                    <div className="bvm-note bvm-note--warn" role="status">
                                        <BiInfoCircle aria-hidden="true" />
                                        <span>An extension order for <b>{currencyFormatter(orderAmount)}</b> was created and never paid. Resume it rather than creating a new one.</span>
                                    </div>
                                    {!prevExtendOrder.extraHours && (
                                        <div className="bvm-field">
                                            <label htmlFor="bvm-resume-hours">Extra hours this order was for</label>
                                            <div className="bvm-inline">
                                                <NumberField
                                                    id="bvm-resume-hours"
                                                    min={0}
                                                    max={remainingHours}
                                                    value={resumeHours}
                                                    onChange={(v) => {
                                                        setResumeHours(String(Math.min(Number(v.replace(/\D/g, "")) || 0, remainingHours) || ""));
                                                        setResumeEstimate(null);
                                                        setResumeEstimateError(null);
                                                    }}
                                                    disabled={paying}
                                                />
                                                <button type="button" className="bv-btn" onClick={previewResumeHours} disabled={paying || resumeEstimating}>
                                                    {resumeEstimating ? <><BiLoaderAlt className="bvm-spin" aria-hidden="true" /> Checking</> : "Check estimate"}
                                                </button>
                                            </div>
                                            <p className="bvm-help">This order predates recording the hours. Enter the value used at the time so the new return time is right, and use Check estimate to compare it with what the order was charged.</p>
                                            {resumeEstimateError && <div className="bvm-note bvm-note--bad" role="alert"><BiErrorCircle aria-hidden="true" /><span>{resumeEstimateError}</span></div>}
                                            {resumeEstimate && (
                                                <div className={`bvm-note ${estimateMismatch ? "bvm-note--warn" : "bvm-note--ok"}`} role="status">
                                                    <BiInfoCircle aria-hidden="true" />
                                                    <span>
                                                        {hoursLabel(resumeHours)} would price at {currencyFormatter(resumeEstimate.total ?? 0)} today; this order was {currencyFormatter(orderAmount)}.
                                                        {estimateMismatch ? " The gap is large, so double-check the hours before paying." : " Close to what this order was charged."}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <button type="button" className="bv-btn bv-btn--go bvm-cta" onClick={payResume} disabled={paying || !hoursValid}>
                                        {paying ? <><BiLoaderAlt className="bvm-spin" aria-hidden="true" /> Opening payment</> : `Resume payment, ${currencyFormatter(orderAmount)}`}
                                    </button>
                                    {confirmCancel ? (
                                        <div className="bvm-help" role="alertdialog" aria-label="Confirm cancelling the extension">
                                            <span>Cancel this unpaid extension? Its payment link stops working and the trip end time is unchanged.</span>
                                            <div className="bvm-actions">
                                                <button type="button" className="bv-btn bv-btn--sm bv-btn--danger" onClick={cancelExtension} disabled={paying || cancelling}>{cancelling ? "Cancelling" : "Cancel extension"}</button>
                                                <button type="button" className="bv-btn bv-btn--sm" onClick={() => setConfirmCancel(false)} disabled={cancelling}>Keep it</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button type="button" className="bv-link bv-link--danger" onClick={() => setConfirmCancel(true)} disabled={paying}>Cancel this extension</button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="bvm-field">
                                        <span className="bvm-label" id="bvm-hours-label">Extra time</span>
                                        <div className="bvm-chips" role="group" aria-labelledby="bvm-hours-label">
                                            {PRESETS.map((h) => (
                                                <button key={h} type="button" className="bvm-chip" aria-pressed={Number(extraHours) === h} onClick={() => setHours(h)} disabled={busy || h > remainingHours}>
                                                    +{h}h
                                                </button>
                                            ))}
                                        </div>
                                        <div className="bvm-stepper">
                                            <button type="button" className="bvm-step" onClick={() => nudge(-1)} disabled={busy || !(Number(extraHours) > 0)} aria-label="One hour less"><BiMinus aria-hidden="true" /></button>
                                            <NumberField
                                                id="bvm-hours"
                                                min={0}
                                                max={remainingHours}
                                                placeholder="Custom"
                                                aria-label="Extra hours"
                                                value={extraHours}
                                                onChange={(v) => setHours(v.replace(/\D/g, ""))}
                                                disabled={busy}
                                            />
                                            <span className="bvm-unit">hours</span>
                                            <button type="button" className="bvm-step" onClick={() => nudge(1)} disabled={busy || Number(extraHours) >= remainingHours} aria-label="One hour more"><BiPlus aria-hidden="true" /></button>
                                        </div>
                                    </div>

                                    <button type="button" className="bv-btn bvm-cta" onClick={getQuote} disabled={busy || !hoursValid}>
                                        {quoting ? <><BiLoaderAlt className="bvm-spin" aria-hidden="true" /> Pricing</> : quote ? "Refresh quote" : "Get quote"}
                                    </button>

                                    {quoteError && <div className="bvm-note bvm-note--bad" role="alert"><BiErrorCircle aria-hidden="true" /><span>{quoteError}</span></div>}

                                    {quote && (
                                        <div className="bvm-quote" role="status">
                                            <dl>
                                                <div><dt>Rental, {hoursLabel(quote.extraHours)}</dt><dd>{currencyFormatter(quote.rental ?? 0)}</dd></div>
                                                {quote.protectionTopUp > 0 && <div><dt>Trip protection top-up</dt><dd>{currencyFormatter(quote.protectionTopUp)}</dd></div>}
                                                <div className="is-total"><dt>Payable now</dt><dd>{currencyFormatter(quote.total ?? 0)}</dd></div>
                                            </dl>
                                            <button type="button" className="bv-btn bv-btn--go bvm-cta" onClick={payFresh} disabled={paying}>
                                                {paying ? <><BiLoaderAlt className="bvm-spin" aria-hidden="true" /> Opening payment</> : `Collect ${currencyFormatter(quote.total ?? 0)} now`}
                                            </button>
                                            <p className="bvm-help">Opens Razorpay checkout on this device. The return time changes once the payment is confirmed.</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="bvm-foot">
                            <button type="button" className="bv-btn" onClick={onClose} disabled={paying}>Close</button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};

export default ExtendTripModal;
